import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { BULK_PHOTO_IMPORT_JOB_TYPE } from '@ooh/contracts';
import type { MediaType } from '@ooh/contracts';
import type { ImportPhase } from './import-phase';
import type { MappingPhaseLabels } from './mapping-phase';
import { UploadPhase } from './upload-phase';
import { ValidationPhase } from './validation-phase';
import { ProgressPhase } from './progress-phase';
import { ResultPhase } from './result-phase';
import type { AmbiguousSupplierEntry } from './supplier-dedup-phase';
import { MappingPhaseSection } from './mapping-phase-section';
import { SupplierDedupSection } from './supplier-dedup-section';
import { useJobStatusLookup } from '../hooks/use-optional-job-status';
import { JobNotFoundState } from './job-not-found-state';
import { isPhotoBundleJob } from './progress-phase';
import { WizardStepper } from './wizard-stepper';
import { buildMappingPhaseLabels } from '../lib/build-mapping-phase-labels';

export type { ImportPhase } from './import-phase';

interface ImportWizardProps {
  importJobId: number | undefined;
  currentPhase: ImportPhase;
  onPhaseChange: (phase: string, jobId?: number, jobType?: string) => void;
  /**
   * Job type known at mount (from the route search params), used so the stepper omits the
   * Validation step on the very first frame, before the job status loads. The resolved job
   * type takes precedence once it loads.
   */
  initialJobType?: string;
  /** Per-file media type override, read from URL search params and passed down. */
  mediaTypeOverride?: MediaType;
  /** Called when user picks/clears the override; parent updates URL. */
  onMediaTypeOverrideChange?: (value: string | null) => void;
}

type NextPhaseDecision =
  | { phase: 'progress'; jobId: number; jobType: string }
  | { phase: 'mapping'; jobId: number }
  | { phase: 'supplier-dedup'; jobId: number; ambiguousSuppliers: AmbiguousSupplierEntry[] }
  | { phase: 'validation'; jobId: number };

/**
 * Resolves which phase the wizard advances to after a successful upload.
 * Exported for unit testing. Pure — the only side effects (onPhaseChange,
 * setAmbiguousSuppliers) live in the ImportWizard dispatcher that consumes this.
 */
export function resolveNextPhaseAfterUpload(args: {
  jobId: number;
  requiresMapping: boolean;
  isPhotoBundle?: boolean;
  ambiguousSuppliers?: AmbiguousSupplierEntry[];
}): NextPhaseDecision {
  const { jobId, requiresMapping, isPhotoBundle, ambiguousSuppliers } = args;
  if (isPhotoBundle === true) {
    return { phase: 'progress', jobId, jobType: BULK_PHOTO_IMPORT_JOB_TYPE };
  }
  if (requiresMapping) {
    return { phase: 'mapping', jobId };
  }
  if (ambiguousSuppliers && ambiguousSuppliers.length > 0) {
    return { phase: 'supplier-dedup', jobId, ambiguousSuppliers };
  }
  return { phase: 'validation', jobId };
}

export function ImportWizard({
  importJobId,
  currentPhase,
  onPhaseChange,
  mediaTypeOverride,
  onMediaTypeOverrideChange,
  initialJobType,
}: ImportWizardProps) {
  const { t } = useTranslation('mediaImport');
  const { job, notFound: jobLookupNotFound } = useJobStatusLookup(
    currentPhase === 'upload' ? undefined : importJobId,
  );
  const isJobNotFound = currentPhase !== 'upload' && (importJobId == null || jobLookupNotFound);
  const isPhotoBundle = isPhotoBundleJob(job?.type ?? initialJobType);

  // Supplier selection — lifted up so supplier change triggers a columns refetch
  const [selectedSupplierId, setSelectedSupplierId] = React.useState<string | null>(null);

  // Transient wizard-session state for the supplier-dedup phase.
  // NOT stored in the URL — the array can be large. A page-refresh or deep-link on the
  // supplier-dedup phase loses this state, so the effect below redirects to validation
  // instead of rendering an empty, already-submittable dedup form (CU-86ca94dta).
  const [ambiguousSuppliers, setAmbiguousSuppliers] = React.useState<AmbiguousSupplierEntry[]>([]);
  const isDedupActive = ambiguousSuppliers.length > 0;

  // Redirect to validation when landing directly on supplier-dedup with no ambiguous
  // suppliers loaded (refresh/deep-link) — otherwise allDecided is vacuously true on an
  // empty decision map and "Dalej" would be enabled with nothing to submit.
  React.useEffect(() => {
    if (
      currentPhase === 'supplier-dedup' &&
      importJobId != null &&
      ambiguousSuppliers.length === 0
    ) {
      onPhaseChange('validation', importJobId);
    }
  }, [currentPhase, importJobId, ambiguousSuppliers.length, onPhaseChange]);

  const handleUploadSuccess = React.useCallback(
    (args: {
      jobId: number;
      requiresMapping: boolean;
      isPhotoBundle?: boolean;
      ambiguousSuppliers?: AmbiguousSupplierEntry[];
    }) => {
      const decision = resolveNextPhaseAfterUpload(args);
      // Reset stale ambiguousSuppliers from a previous upload in every branch except
      // supplier-dedup itself — handleBackToUpload also resets it, but a user can call
      // onNext from UploadPhase again without an intervening back-to-upload round trip.
      if (decision.phase === 'supplier-dedup') {
        setAmbiguousSuppliers(decision.ambiguousSuppliers);
      } else {
        setAmbiguousSuppliers([]);
      }
      switch (decision.phase) {
        case 'progress':
          // Carry the job type forward so the progress phase + stepper render the photo layout
          // on the first frame, before the status poll returns (avoids a brief XLSX-layout flash).
          onPhaseChange('progress', decision.jobId, decision.jobType);
          break;
        case 'mapping':
          onPhaseChange('mapping', decision.jobId);
          break;
        case 'supplier-dedup':
          onPhaseChange('supplier-dedup', decision.jobId);
          break;
        case 'validation':
          onPhaseChange('validation', decision.jobId);
          break;
      }
    },
    [onPhaseChange],
  );

  const handleSupplierChange = React.useCallback((supplierId: string | null) => {
    setSelectedSupplierId(supplierId);
  }, []);

  const handleValidationDone = React.useCallback(() => {
    onPhaseChange('progress');
  }, [onPhaseChange]);

  // Returns to 'upload', which clears importJobId in the route and resets the wizard so the user
  // can fix the file and re-upload without a full page refresh (CU-86ca8125j). Shared by the
  // validation-phase back button and the photo-bundle result "back to import" button.
  // Also clears ambiguousSuppliers so a re-upload never carries over the previous job's stale
  // dedup entries against the new importJobId.
  const handleBackToUpload = React.useCallback(() => {
    setAmbiguousSuppliers([]);
    onPhaseChange('upload', undefined);
  }, [onPhaseChange]);

  const handleImportComplete = React.useCallback(() => {
    onPhaseChange('result');
  }, [onPhaseChange]);

  // Shared callback: both mapping confirmation and dedup submission advance to validation.
  const handleAdvanceToValidation = React.useCallback(() => {
    onPhaseChange('validation', importJobId);
  }, [onPhaseChange, importJobId]);

  const mappingLabels: MappingPhaseLabels = buildMappingPhaseLabels(t);

  // Nested function (not React.useCallback) so ESLint's `complexity` rule scores it
  // independently from ImportWizard's own body — keeps the phase-dispatch branching
  // out of the component function's complexity count.
  function renderActivePhase(): React.ReactNode {
    if (currentPhase === 'upload') return <UploadPhase onNext={handleUploadSuccess} />;
    if (isJobNotFound || importJobId == null) {
      return <JobNotFoundState onBackToImport={handleBackToUpload} />;
    }
    if (currentPhase === 'supplier-dedup') {
      return ambiguousSuppliers.length > 0 ? (
        <SupplierDedupSection
          importJobId={importJobId}
          ambiguousSuppliers={ambiguousSuppliers}
          onNext={handleAdvanceToValidation}
        />
      ) : null;
    }
    if (currentPhase === 'mapping') {
      return (
        <MappingPhaseSection
          importJobId={importJobId}
          selectedSupplierId={selectedSupplierId}
          onSupplierChange={handleSupplierChange}
          onConfirmed={handleAdvanceToValidation}
          onPhaseChange={onPhaseChange}
          errorLabel={t('mapping.errorMissingRequired')}
          mappingLabels={mappingLabels}
          mediaTypeOverride={mediaTypeOverride}
          onMediaTypeOverrideChange={onMediaTypeOverrideChange}
        />
      );
    }
    if (currentPhase === 'validation') {
      return (
        <ValidationPhase
          importJobId={importJobId}
          onNext={handleValidationDone}
          onBack={handleBackToUpload}
        />
      );
    }
    if (currentPhase === 'progress') {
      return <ProgressPhase importJobId={importJobId} onComplete={handleImportComplete} />;
    }
    if (currentPhase === 'result') {
      return <ResultPhase importJobId={importJobId} onBackToImport={handleBackToUpload} />;
    }
    return null;
  }

  return (
    <div className="flex flex-col h-full" data-testid="media-import">
      <div className="shrink-0 px-6 pt-6 pb-4 border-b border-border/60">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <h1 className="text-[15px] font-semibold" data-testid="page-heading">
              {t('pageTitle')}
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">{t('pageSubtitle')}</p>
          </div>
        </div>
        {!isJobNotFound && (
          <WizardStepper
            currentPhase={currentPhase}
            isPhotoBundle={isPhotoBundle}
            isDedupActive={isDedupActive}
            labels={{
              upload: t('stepper.upload'),
              supplierDedup: t('stepper.supplierDedup'),
              mapping: t('stepper.mapping'),
              validation: t('stepper.validation'),
              progress: t('stepper.progress'),
              result: t('stepper.result'),
              ariaLabel: t('pageTitle'),
            }}
          />
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6">{renderActivePhase()}</div>
    </div>
  );
}
